import React, { useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import MDialog from './MDialog';
import { MButton } from './MButtons';
import { IconUpload, IconTrash } from '@tabler/icons-react';

interface MAvatarEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (file: File) => Promise<void>;
  initialImage?: string;
  onDelete?: () => Promise<void> | void;
}

const MAvatarEditorDialog: React.FC<MAvatarEditorDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  initialImage = '',
  onDelete,
}) => {
  const editorRef = useRef<AvatarEditor>(null);
  const [image, setImage] = useState<string | File | null>(initialImage || null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [hasChanged, setHasChanged] = useState(false);

  // Ensure image updates when initialImage changes (e.g., after avatar update or dialog reopen)
  React.useEffect(() => {
    if (!hasChanged) {
      setImage(initialImage || null);
    }
  }, [initialImage, hasChanged]);
  const [scale, setScale] = useState(1.2);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
      setOriginalFile(e.target.files[0]);
      setHasChanged(true);
      setError(null);
    }
  };

  const handleDeleteClick = () => {
    if (!onDelete) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    try {
      setDeleting(true);
      setError(null);
      await onDelete();
      setImage(null);
      setOriginalFile(null);
      setHasChanged(false);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError('Failed to delete image.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleSave = async () => {
    if (!editorRef.current) return;
    try {
      setSaving(true);
      const canvas = editorRef.current.getImageScaledToCanvas();
      canvas.toBlob(async (blob) => {
        if (blob) {
          // Use original file name and type if available
          let fileName = 'avatar.png';
          let fileType = 'image/png';
          if (originalFile) {
            fileName = originalFile.name;
            fileType = originalFile.type || 'image/png';
          }
          const file = new File([blob], fileName, { type: fileType });
          await onSave(file);
          onClose();
        } else {
          setError('Failed to generate image.');
        }
        setSaving(false);
      }, originalFile?.type || 'image/png');
    } catch (err) {
      setError('Failed to save image.');
      setSaving(false);
    }
  };


  return (
    <MDialog
      isOpen={isOpen}
      onClose={onClose}
      title=""
      primaryCtaText={saving ? 'Saving...' : 'Save'}
      onPrimaryCta={handleSave}
      isPrimaryCtaLoading={saving || deleting}
      isPrimaryCtaDisabled={!hasChanged || saving || deleting}
      className="gap-0"
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
        <input
          id="avatar-upload-input"
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <div
          style={{
            width: 192,
            height: 192,
            borderRadius: 24,
            background: 'lightgray 50% / cover no-repeat',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {image ? (
            <AvatarEditor
              ref={editorRef}
              image={image}
              width={200}
              height={200}
              border={30}
              borderRadius={120}
              color={[255, 255, 255, 0.6]}
              scale={hasChanged ? scale : 1}
              rotate={0}
            />
          ) : (
            <img
              src="/assets/avatar.png"
              alt="Avatar placeholder"
              style={{
                width: 192,
                height: 192,
                objectFit: 'cover',
                borderRadius: 120,
              }}
            />
          )}
        </div>

        {/* Action buttons row: Delete / Change photo */}
        <div style={{ display: 'flex', gap: 16, width: '100%', justifyContent: 'center' }}>
          <MButton
            type="button"
            variant="outline"
            size="md"
            leftIcon={<IconTrash size={18} />}
            onClick={handleDeleteClick}
            disabled={!onDelete || deleting || saving}
            className="flex-1"
            style={{ whiteSpace: 'nowrap', flexShrink: 0, width: '45%' }}
          >
            Delete photo
          </MButton>
          <MButton
            type="button"
            variant="outline"
            size="md"
            leftIcon={<IconUpload size={18} />}
            onClick={() => document.getElementById('avatar-upload-input')?.click()}
            disabled={saving || deleting}
            className="flex-1"
            style={{ whiteSpace: 'nowrap', flexShrink: 0, width: '45%' }}
          >
            {image ? 'Change photo' : 'Upload photo'}
          </MButton>
        </div>

        {hasChanged && (
          <div style={{ marginTop: 4 }}>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={scale}
              onChange={e => setScale(Number(e.target.value))}
              style={{ width: 160 }}
            />
          </div>
        )}
        {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
      </div>

      {showDeleteConfirm && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <MDialog
            isOpen={showDeleteConfirm}
            onClose={handleCancelDelete}
            title="Delete photo?"
            description="This photo will be removed from your profile."
            primaryCtaText={deleting ? 'Yes' : 'Yes'}
            onPrimaryCta={handleConfirmDelete}
            isPrimaryCtaLoading={deleting}
            secondaryCtaText="No"
            onSecondaryCta={handleCancelDelete}
          />
        </div>
      )}
    </MDialog>
  );
};

export default MAvatarEditorDialog;
